import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { normalizeCas } from "../mapping/conditionRowMerge"

/**
 * CAS (normalized, see `normalizeCas`) -> translation key, for the ten RoHS
 * Annex II restricted substances (RoHS 3 / Delegated Directive (EU)
 * 2015/863). `mc_universal_substances` carries no localised names
 * (`exemption_repair.py` writes the register's English master name only),
 * so the condition slot showed "Lead" verbatim in the German mask (Pablo,
 * TUR-1211 H4/B5). This is a small, closed lookup for exactly these ten
 * identities, cross-checked against `backend/alembic/data/
 * mc_universal_substances.json` (2026-09-15): any other CAS keeps the
 * extracted name as-is. The CAS stays the canonical persisted identity
 * either way; only the display text changes.
 *
 * The register's own generic "Polybrominated diphenyl ethers" entry carries
 * no CAS at all (`cas_number: null`), the only PBDE-family entry that does
 * carry one is decaBDE (1163-19-5), so that is the CAS this table keys the
 * PBDE display name to.
 */
const ROHS10_KEYS: Record<string, TranslationKeys> = {
  "7439-92-1": "MC_SUBSTANCE_7439-92-1", // Lead
  "7440-43-9": "MC_SUBSTANCE_7440-43-9", // Cadmium
  "7439-97-6": "MC_SUBSTANCE_7439-97-6", // Mercury
  "18540-29-9": "MC_SUBSTANCE_18540-29-9", // Hexavalent chromium
  "59536-65-1": "MC_SUBSTANCE_59536-65-1", // Polybrominated biphenyls (PBB)
  "1163-19-5": "MC_SUBSTANCE_1163-19-5", // Polybrominated diphenyl ethers (PBDE), via decaBDE's CAS
  "117-81-7": "MC_SUBSTANCE_117-81-7", // Bis(2-ethylhexyl) phthalate (DEHP)
  "85-68-7": "MC_SUBSTANCE_85-68-7", // Benzyl butyl phthalate (BBP)
  "84-74-2": "MC_SUBSTANCE_84-74-2", // Dibutyl phthalate (DBP)
  "84-69-5": "MC_SUBSTANCE_84-69-5", // Diisobutyl phthalate (DIBP)
}

/**
 * CAS -> recognised name variants for that SAME substance, lowercase. Used
 * only to gate localisation (see `substanceDisplayName` below), never as an
 * address or an identity source of any kind.
 *
 * WHY THIS EXISTS (HIGH finding, adversarial review, 2026-09-15): the first
 * cut of this helper translated on the CAS alone, so a disclosure whose CAS
 * was mis-cited (a real, observed failure mode; the document names one
 * substance and cites another's CAS by mistake) rendered under the WRONG
 * substance's name, and the editor's dedupe then silently swallowed a
 * second, correctly-named disclosure sharing that same wrong CAS into one
 * label. The backend repair (`exemption_repair.py` / the D6/M3 pipeline)
 * deliberately leaves such a conflict standing rather than resolving it
 * silently, so the document's own wording wins; this display layer must not
 * quietly launder it into the "expected" name for the CAS.
 */
const ROHS10_ALIASES: Record<string, readonly string[]> = {
  "7439-92-1": ["lead", "blei", "pb", "鉛"],
  "7440-43-9": ["cadmium", "cd", "カドミウム"],
  "7439-97-6": ["mercury", "quecksilber", "hg", "水銀"],
  "18540-29-9": [
    "hexavalent chromium",
    "chromium vi",
    "chrom vi",
    "chrom(vi)",
    "cr(vi)",
    "cr vi",
    "六価クロム",
  ],
  "59536-65-1": ["pbb", "polybrominated biphenyls", "polybromierte biphenyle"],
  "1163-19-5": [
    "pbde",
    "polybrominated diphenyl ethers",
    "polybromierte diphenylether",
    "decabde",
  ],
  "117-81-7": [
    "dehp",
    "bis(2-ethylhexyl) phthalate",
    "bis(2-ethylhexyl)phthalat",
    "di(2-ethylhexyl) phthalate",
  ],
  "85-68-7": ["bbp", "benzyl butyl phthalate", "benzylbutylphthalat"],
  "84-74-2": ["dbp", "dibutyl phthalate", "dibutylphthalat"],
  "84-69-5": ["dibp", "diisobutyl phthalate", "diisobutylphthalat"],
}

/** True when `alias` occurs in `haystack` (both already lowercase) as a
 *  WHOLE WORD, not merely a substring: not immediately preceded or followed
 *  by another ASCII letter or digit. Catches "Blei (Pb)" and "Lead, in
 *  glass" (alias bounded by punctuation/whitespace/string edges) while
 *  rejecting "Leaded glass" (alias immediately followed by "ed"). CJK
 *  aliases (no ASCII letters either side) always satisfy this boundary
 *  check by construction; a CJK compound word that happens to CONTAIN one
 *  of them (no whitespace to separate CJK "words") is a known, accepted
 *  limitation, out of scope for the RoHS-10 alias list actually in use. */
function containsAliasWord(haystack: string, alias: string): boolean {
  const escaped = alias.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  return new RegExp(`(?<![a-z0-9])${escaped}(?![a-z0-9])`).test(haystack)
}

/**
 * The substance name to show in the studio mask: the catalogue's own
 * translation for a known RoHS-10 CAS, but ONLY when the incoming name is
 * blank OR is itself a recognised alias of that CAS's substance (case-
 * insensitive, matched as a whole word, see `containsAliasWord`); a name
 * that does not match keeps its own wording unchanged, exactly like an
 * unknown CAS does, so a document-vs-CAS conflict stays visible rather than
 * being silently normalised away (HIGH finding, 2026-09-15). `translate`
 * already resolves per the viewer's current language (the catalogue's own
 * EN entries carry the same master name the register writes today), so this
 * never branches on language itself. Frontend display only: this never
 * touches what is persisted (TUR-1211 H4/B5; the backend registry and
 * `exemption_repair.py` are unchanged).
 */
export function substanceDisplayName(
  name: string,
  cas: string | null,
  translate: (key: TranslationKeys) => string,
): string {
  const normalized = normalizeCas(cas) ?? ""
  const key = ROHS10_KEYS[normalized]
  if (!key) return name
  const trimmed = name.trim()
  if (trimmed === "") return translate(key)
  const aliases = ROHS10_ALIASES[normalized] ?? []
  const lowered = trimmed.toLowerCase()
  return aliases.some((alias) => containsAliasWord(lowered, alias))
    ? translate(key)
    : name
}
